import { COLORS } from '../../constants/theme';
import { useToast } from '../../hooks/useToast';
import { useGatherIntel, type ButtonState } from '../../hooks/useGatherIntel';

const CB_COLOR = '#6b4fa0';
const SOM_COLOR = '#4a7a5a';
const SV_COLOR = '#7a6a40';

interface IntelButtonsProps {
  targetProvinceId: string;
  worldId: string;
  playerProvinceId: string;
  onIntelGathered?: () => void;
  /**
   * SF-105: disables the thievery-based intel ops (SM/SV) when the target is protected -
   * ThieveryAction has no intel exemption (Phase 3 decision). CB (Crystal Ball) stays enabled
   * regardless, mirroring CastSpellAction's intel-spell exemption from the protection gate.
   */
  thieveryDisabled?: boolean;
}

export function IntelButtons({ targetProvinceId, worldId, playerProvinceId, onIntelGathered, thieveryDisabled }: IntelButtonsProps) {
  const { addToast } = useToast();
  const { gather, state } = useGatherIntel(worldId, playerProvinceId, targetProvinceId, {
    onResult: addToast,
    onGathered: onIntelGathered,
  });

  return (
    <div style={{ display: 'flex', gap: '4px' }}>
      <IntelButton
        label="CB"
        color={CB_COLOR}
        state={state.CB}
        onClick={() => gather('CB')}
      />
      <IntelButton
        label="SM"
        color={SOM_COLOR}
        state={state.SOM}
        disabled={thieveryDisabled}
        onClick={() => gather('SOM')}
      />
      <IntelButton
        label="SV"
        color={SV_COLOR}
        state={state.SURVEY}
        disabled={thieveryDisabled}
        onClick={() => gather('SURVEY')}
      />
    </div>
  );
}

function IntelButton({ label, color, state, onClick, disabled }: {
  label: string;
  color: string;
  state: ButtonState;
  onClick: () => void;
  disabled?: boolean;
}) {
  const isDone = state === 'done';
  const isLoading = state === 'loading';
  const isDisabled = isLoading || disabled;

  return (
    <button
      onClick={e => { e.stopPropagation(); onClick(); }}
      disabled={isDisabled}
      title={disabled ? `${label} (target protected)` : label}
      style={{
        width: '28px',
        height: '24px',
        borderRadius: '3px',
        border: `1px solid ${isDone ? color : COLORS.border}`,
        backgroundColor: isDone ? `${color}22` : 'transparent',
        color: isDone ? color : COLORS.text.muted,
        fontSize: '9px',
        fontWeight: 600,
        cursor: isLoading ? 'wait' : disabled ? 'not-allowed' : 'pointer',
        opacity: disabled && !isDone ? 0.4 : 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: isDone ? `0 0 4px ${color}44` : 'none',
      }}
    >
      {isLoading ? '...' : label}
    </button>
  );
}
